import { isBrowser } from 'react-device-detect';
import AppRoutes from 'routing/AppRoutes';
import AppRouter from 'routing/AppRouter';
import { ErrorBoundary } from 'react-error-boundary';
import CssBaseline from '@mui/material/CssBaseline';
import FullpageErrorFallBack from './errorPage';
import PATH from 'crono-fe-common/constants/path';
import { withBasename } from 'utils/basename';
import { ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import ConfirmModalProvider from 'context/confirmModal';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { FC } from 'react';
import type { BrowserHistory } from 'history';
import { GoogleOAuthProvider } from '@react-oauth/google';
import AnalyticsProvider from 'context/analytics';
import AccountDrawerProvider from 'context/accountDrawer';
import AuthProvider from 'context/auth';
import FiltersProvider from 'pages/home/context/filters';
import EmailThreadProvider from 'context/EmailThreadTab';
import ExecuteMultipleTasksProvider from 'pages/accountDrawer/startMultipleTasksContext';
import { theme } from 'crono-fe-common/theme';
import LinkedinProvider from 'context/linkedinSingleMessageTab';
import OnboardingFeaturesProvider from 'context/onboardingFeatures';
import HideTaskConstructionProvider from 'pages/accountDrawer/hideTaskConstructionContext';
import InMailReplyProvider from 'context/inMailReplyTab';
import MobileNotSupportedPage from 'pages/mobileNotSupported';
import ResolutionNotSupportedPage from 'pages/resolutionNotSupported';
import useScreenSize from 'hooks/useScreenSize';
import { isLocal } from '../utils/uiHelpers';
import PollerProvider from 'context/poller';
import UpgradeModalProvider from 'context/upgradeModal';
import UpgradeSubscriptionModal from 'pages/subscription/upgradeSubscription';
import StrategyAutomationsProvider from 'context/strategyAutomations';
import AiVariablePollerProvider from 'context/aiVariablesPoller';
import ListsProvider from 'context/lists';
import CustomLocationProvider from 'context/customLocation';
import LinkedinLogsConversationProvider from 'context/linkedinLogsConversation';
import EnabledProvidersProvider from 'context/enabledProviders';
import { CronoCallFloatingUI, CronoCallProvider } from 'components/CronoCall';
import CrmPushProvider from 'context/crmPush';
import { queryClient } from 'utils/queryClient';
import KeepAliveProvider from 'context/keepAlive';
import ChatProvider from 'context/chatRef';
import FindNewTabProvider from 'pages/home/context/findNewContext';
import { StrategyFiltersProvider } from 'pages/strategy/filters/strategyFiltersContext';
import { RingoverProvider } from 'context/ringover';
import { RingoverCallFloatingUI } from 'components/Ringover/RingoverCallFloatingUI';
import { AircallProvider } from 'context/aircall';
import { AircallCallFloatingUI } from 'components/Aircall/AircallFloatingUI';
import { TooltipProvider } from '@crono-one/design-system';
import { Toaster } from '@crono-one/design-system/side-effects';
import AccountDrawer from 'pages/accountDrawer';
import AccountDrawerControllerProvider from 'context/accountDrawerController';
import { CronoAiChatProvider } from '@crono-one/ai-chat';
import '@crono-one/ai-chat/styles.css';

//This is a procedure to capture console logs so that we can track errors better in analytics
const consoleOutput: {
  type: string;
  message: string[];
}[] = [];
const originalError = console.error;
console.error = function (...args) {
  if (!isLocal()) {
    //ConsoleOutput should have max 15 elements inside
    if (consoleOutput.length > 15) {
      consoleOutput.shift();
    }
    consoleOutput.push({
      type: 'error',
      message: args.map((singleMessage) => {
        if (typeof singleMessage === 'string') {
          return singleMessage.substring(0, 700);
        } else if (singleMessage !== null && singleMessage !== undefined) {
          return String(singleMessage).substring(0, 700);
        } else {
          return String(singleMessage);
        }
      }),
    });
  }
  originalError.apply(console, args);
};
export function getConsoleOutput() {
  return consoleOutput;
}

const App: FC<{ history: BrowserHistory }> = ({ history }) => {
  const onResetErrorBoundary = () => {
    history.push(withBasename(PATH.HOME));
  };

  const { supportedResolution } = useScreenSize();
  //Check if current pathname is opt_out. If so allow also in mobile phone
  const isOptOut = history.location.pathname
    .toLowerCase()
    .includes(PATH.OPT_OUT);

  return (
    <AnalyticsProvider>
      <ErrorBoundary
        FallbackComponent={FullpageErrorFallBack}
        onReset={onResetErrorBoundary}
      >
        <CssBaseline />
        <ThemeProvider theme={theme}>
          {isBrowser || isOptOut || isLocal() ? (
            <UpgradeModalProvider>
              <QueryClientProvider client={queryClient}>
                <CronoAiChatProvider>
                  <GoogleOAuthProvider
                    clientId={process.env.VITE_APP_GOOGLE_CLIENT_ID ?? ''}
                  >
                    <CustomLocationProvider>
                      <StrategyAutomationsProvider>
                        <ConfirmModalProvider>
                          {supportedResolution || isOptOut || isLocal() ? (
                            <div className="App">
                              <AppRouter history={history}>
                                <AuthProvider>
                                  <ChatProvider>
                                    <KeepAliveProvider>
                                      <CrmPushProvider>
                                        <EnabledProvidersProvider>
                                          <AiVariablePollerProvider>
                                            <StrategyFiltersProvider>
                                              <ListsProvider>
                                                <FiltersProvider>
                                                  <HideTaskConstructionProvider>
                                                    <EmailThreadProvider>
                                                      <LinkedinLogsConversationProvider>
                                                        <LinkedinProvider>
                                                          <InMailReplyProvider>
                                                            <CronoCallProvider>
                                                              <RingoverProvider>
                                                                <AircallProvider>
                                                                  <TooltipProvider>
                                                                    <AccountDrawerControllerProvider>
                                                                      <ExecuteMultipleTasksProvider>
                                                                        <AccountDrawerProvider
                                                                          content={
                                                                            <AccountDrawer />
                                                                          }
                                                                        >
                                                                          <OnboardingFeaturesProvider>
                                                                            <PollerProvider>
                                                                              <FindNewTabProvider>
                                                                                <BodyComponent />
                                                                              </FindNewTabProvider>
                                                                            </PollerProvider>
                                                                          </OnboardingFeaturesProvider>
                                                                        </AccountDrawerProvider>
                                                                      </ExecuteMultipleTasksProvider>
                                                                    </AccountDrawerControllerProvider>
                                                                  </TooltipProvider>
                                                                </AircallProvider>
                                                              </RingoverProvider>
                                                            </CronoCallProvider>
                                                          </InMailReplyProvider>
                                                        </LinkedinProvider>
                                                      </LinkedinLogsConversationProvider>
                                                    </EmailThreadProvider>
                                                  </HideTaskConstructionProvider>
                                                </FiltersProvider>
                                              </ListsProvider>
                                            </StrategyFiltersProvider>
                                          </AiVariablePollerProvider>
                                        </EnabledProvidersProvider>
                                      </CrmPushProvider>
                                    </KeepAliveProvider>
                                  </ChatProvider>
                                </AuthProvider>
                              </AppRouter>
                            </div>
                          ) : (
                            <ResolutionNotSupportedPage />
                          )}
                          <ReactQueryDevtools />
                        </ConfirmModalProvider>
                      </StrategyAutomationsProvider>
                    </CustomLocationProvider>
                  </GoogleOAuthProvider>
                </CronoAiChatProvider>
              </QueryClientProvider>
            </UpgradeModalProvider>
          ) : (
            <MobileNotSupportedPage />
          )}
        </ThemeProvider>
      </ErrorBoundary>
    </AnalyticsProvider>
  );
};

const BodyComponent = () => {
  return (
    <>
      <UpgradeSubscriptionModal />
      {/*<AWSWAFCaptchaModal /> <- Commented at the moment since it was overriding console logs (no logs visible in the console). To be solved if decide to uncomment.*/}
      <AppRoutes />
      <CronoCallFloatingUI />
      <RingoverCallFloatingUI />
      <AircallCallFloatingUI />
      <Toaster />
    </>
  );
};

export default App;
